/**
 * English (en-US) translation.
 * Must implement EXACTLY the same shape as `pt.ts` (enforced by `Translations` type).
 */
import type { Translations } from '..';

const en: Translations = {
  meta: {
    htmlLang: 'en-US',
  },
  common: {
    loading: 'Loading...',
    buy: 'Buy',
    buyNow: 'Buy Now',
    startNow: 'Get Started',
    downloadApp: 'Download the App',
    getMyChip: 'I Want My SIM',
    iWantMyChip: 'I want my SIM',
    close: 'Close',
    cancel: 'Cancel',
    unlimited: 'Unlimited',
  },
  layout: {
    loading: 'Loading...',
    errorTitle: 'Failed to load configuration',
  },
  topBar: {
    customerService: 'Support',
    helpCenter: 'Help Center',
    recharges: 'Top-Ups',
    portability: 'Number Transfer',
    defaultPromo: 'YOU CAN NOW MANAGE YOUR LINES IN OUR NEW APP',
    downloadNowCta: 'DOWNLOAD NOW',
  },
  header: {
    home: 'Home',
    plans: 'Plans',
    portability: 'Number Transfer',
    advantages: 'Benefits',
    recharge: 'Top-Up',
    customerService: 'Support',
    helpCenter: 'Help Center',
  },
  hero: {
    defaultHeadline: 'You more',
    defaultHeadlineBold: 'connected',
    defaultSubtitle: 'The carrier that knows how to connect you to the world!',
    defaultPlanLabel: 'PRO PLAN',
    defaultPlanData: '45',
    defaultPlanUnit: 'gb',
    defaultPlanFeatures: 'Internet + SMS + Calls',
    defaultPlanBenefits: 'And 12 more amazing benefits',
    defaultPlanPrice: '$39.99',
    defaultPlanInstallment: 'or 29.90 on the annual plan',
    defaultAppCta: 'Get started right now with the app',
    defaultCurvedText: 'Manage everything with the White Telecom app',
    defaultDisclaimer:
      'Offer valid for a limited time. Check conditions, coverage and availability in your area. Prices subject to change without notice. Images are for illustration only.',
    defaultPromoText: 'Enjoy our promotion with +15GB',
    defaultPriceLabel: 'Only',
    fallbackApps: 'Unlimited Whatsapp, Unlimited Waze',
  },
  plans: {
    tag: 'Plans & Benefits',
    titleLine1: 'Much more than a plan,',
    titleLine2: 'possibilities to help you do more',
    mostPopular: 'Most Popular',
    appsIncluded: 'Apps Included',
    perMonth: 'per month',
  },
  benefits: {
    tag: 'Benefits',
    titleLine1: 'Benefits already included',
    titleLine2: 'with your plan',
    items: [
      { title: 'Unlimited Whatsapp', description: 'Use it every day without spending your data.' },
      { title: 'No Long-Term Contracts', description: 'Freedom to cancel whenever you want, no fees.' },
      { title: 'Support That Actually Helps', description: 'Fast, direct support — no runaround.' },
      { title: 'Roll Over Your Unused Data', description: 'Any data you do not use comes back to you.' },
      { title: 'Fast 5G Connection', description: 'More speed and stability wherever you are.' },
      { title: '100% Digital Experience', description: 'Solve everything from the app, no paperwork.' },
      { title: 'Physical or Digital SIM', description: 'Choose between a physical SIM or eSIM.' },
      { title: 'Unlimited Calls and SMS', description: 'Talk and text with no limits.' },
    ],
  },
  features: {
    tag: 'Benefits',
    titleLine1: 'We thought of every detail',
    titleLine2: 'for you',
    physicalChip: 'Physical SIM',
    eSim: 'eSIM',
    chipTitle: 'Activate your SIM in minutes.',
    chipDesc: 'Choose between a physical SIM or eSIM and start using it right away.',
    portabilityTag: 'Number Transfer',
    portabilityTitle: 'Bring your number to {{brand}}.',
    portabilityDesc: 'Choose between a physical SIM or eSIM and start using it right away.',
    aboutTag: 'ABOUT US',
    aboutTitleLine1: 'A carrier',
    aboutTitleLine2: 'built to',
    aboutTitleLine3: 'simplify your',
    aboutTitleLine4: 'life.',
  },
  appSection: {
    titleLine1: 'Control everything from',
    titleLine2: 'the app.',
    appBadges: ['Balance', 'Top-Ups', 'Lines', 'Payments', 'Number Transfer', 'Rewards Club'],
    activateHint: 'Download the app and activate your SIM in minutes.',
    socialProofTag: 'People',
    socialProofTitle: 'More and more people are choosing {{brand}}.',
    socialProofFallbackBrand: 'our carrier',
    socialProofFooter: 'Thousands of people are already connected.',
    ratingSuffix: 'app rating',
    ratingFooter: 'Our app rating across the app stores.',
    purpleTitleLine1: 'Mobile internet without',
    purpleTitleLine2: 'the headache.',
  },
  faq: {
    tag: 'Frequently Asked Questions',
    titleLine1: 'Have questions?',
    titleLine2: 'We can help',
    subtitle: 'Here are the most common questions we receive',
    viewAnswer: 'See Answer',
    fallback: [
      { category: 'Number Transfer', question: 'Can I transfer my number from any carrier?', answer: 'Yes! You can bring your number from any carrier to our network, completely free and hassle-free, directly through the app.' },
      { category: 'Coverage', question: 'Do you have coverage in my city?', answer: 'We have the widest coverage in the country, reaching 100% of municipalities through our partner network with 4G and 5G technology.' },
      { category: 'Number Transfer', question: 'Can I keep my current number?', answer: 'Absolutely! When activating your new SIM or eSIM, you choose to keep your number, and we take care of the entire transition.' },
      { category: 'Plans', question: 'What happens when my data runs out?', answer: 'You stay connected! Browsing speed is reduced, but you keep access. If you prefer, you can buy additional packs instantly from the app.' },
      { category: 'Subscription', question: 'Can I cancel anytime?', answer: 'Yes. We believe in freedom for our customers. Our plans have no long-term commitments or hidden cancellation fees.' },
    ],
    legalTag: 'Legal',
  },
  footer: {
    support: 'Support',
    helpCenter: 'Help Center',
    customerService: 'Customer Service',
    copyrightSuffix: 'All rights reserved',
    cnpjLabel: 'Tax ID',
    defaultCompany: 'White Telecom',
  },
  marquee: {
    defaultText: 'You — much more connected',
  },
  rechargeModal: {
    title: 'Top Up',
    successTitle: 'Top-up requested!',
    copyLink: 'Copy Link',
    payNow: 'Pay Now',
    linkCopied: 'Link copied to clipboard!',
    phoneLabel: 'Phone Number',
    phonePlaceholder: '(11) 99999-9999',
    planLabel: 'Plan',
    loadingPlans: 'Loading plans...',
    submitButton: 'Top Up',
    errorInvalidPhone: 'Enter a valid phone number including the area code.',
    errorSelectPlan: 'Select a plan.',
    errorGeneric: 'Error processing the top-up. Please try again.',
  },
  portabilityModal: {
    title: 'Request Number Transfer',
    successTitle: 'Number transfer requested!',
    protocolLabel: 'Reference',
    yourNumberLabel: 'Your number at {{brand}}',
    yourNumberFallbackBrand: 'carrier',
    donorNumberLabel: 'Number to be transferred (current carrier)',
    donorOperatorLabel: 'Current carrier',
    loadingOperators: 'Loading carriers...',
    submitButton: 'Request Transfer',
    errorInvalidContel: 'Enter a valid number at your new carrier including the area code.',
    errorInvalidDonor: 'Enter a valid current (donor) number including the area code.',
    errorSelectOperator: 'Select the current carrier.',
    errorGeneric: 'Error requesting the number transfer. Please try again.',
  },
  legalModal: {
    placeholderContent: 'Content not configured.',
  },
  page404: {
    title: '404',
    subtitle: 'Page not found',
    back: 'Back to home',
  },
  languageSwitcher: {
    label: 'Language',
    pt: 'Portuguese',
    en: 'English',
    es: 'Spanish',
  },
  whatsapp: {
    salesMessage: 'I would like to sign up for a new plan from {{brand}}',
    salesFallbackBrand: 'your carrier',
  },
};

export default en;
